import type { EmailConfiguration } from '@discover/database'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'

interface CycleEmailConfigurationsState {
	useCreateConfiguration: () => {
		createConfiguration: (input: {
			cycleId: string
			emailTemplateId: string
			configuration: EmailConfiguration
			onSuccess?: () => void
		}) => void
		isCreating: boolean
	}

	useUpdateConfiguration: () => {
		updateConfiguration: (input: {
			id: string
			cycleId: string
			emailTemplateId: string
			configuration: EmailConfiguration
			onSuccess?: () => void
		}) => void
		isUpdating: boolean
	}

	useDeleteConfiguration: () => {
		deleteConfiguration: (input: { id: string; cycleId: string; onSuccess?: () => void }) => void
		isDeleting: boolean
	}

	useSetEnabled: () => {
		setEnabled: (input: { id: string; cycleId: string; enabled: boolean }) => void
		isSettingEnabled: boolean
	}
}

export const useCycleEmailConfigurationsStore = create<CycleEmailConfigurationsState>()(
	devtools(
		immer(() => ({
			useCreateConfiguration: () => {
				const queryClient = useQueryClient()
				const toast = useToast()

				const mutation = useMutation(
					trpc.admin.cycles.emails.configurations.create.mutationOptions({
						onSuccess: () => {
							toast.showToast('Email configuration created', 'info')
						},
						onError: (error) => {
							toast.showToast(error.message ?? 'Failed to create email configuration', 'error')
						},
						onSettled: (_data, _error, variables) => {
							queryClient.invalidateQueries({
								queryKey: trpc.admin.cycles.emails.configurations.list.queryKey({
									cycleId: variables.cycleId,
								}),
								exact: false,
								refetchType: 'all',
							})
						},
					}),
				)

				return {
					createConfiguration: ({ onSuccess, ...input }) => {
						mutation.mutate(input, { onSuccess })
					},
					isCreating: mutation.isPending,
				}
			},

			useUpdateConfiguration: () => {
				const queryClient = useQueryClient()
				const toast = useToast()

				const mutation = useMutation(
					trpc.admin.cycles.emails.configurations.update.mutationOptions({
						onSuccess: () => {
							toast.showToast('Email configuration updated', 'info')
						},
						onError: (error) => {
							toast.showToast(error.message ?? 'Failed to update email configuration', 'error')
						},
						onSettled: (_data, _error, variables) => {
							queryClient.invalidateQueries({
								queryKey: trpc.admin.cycles.emails.configurations.list.queryKey({
									cycleId: variables.cycleId,
								}),
								exact: false,
								refetchType: 'all',
							})
						},
					}),
				)

				return {
					updateConfiguration: ({ onSuccess, ...input }) => mutation.mutate(input, { onSuccess }),
					isUpdating: mutation.isPending,
				}
			},

			useDeleteConfiguration: () => {
				const queryClient = useQueryClient()
				const toast = useToast()

				const mutation = useMutation(
					trpc.admin.cycles.emails.configurations.delete.mutationOptions({
						onSuccess: () => {
							toast.showToast('Email configuration deleted', 'info')
						},
						onError: (error) => {
							toast.showToast(error.message ?? 'Failed to delete email configuration', 'error')
						},
						onSettled: (_data, _error, variables) => {
							queryClient.invalidateQueries({
								queryKey: trpc.admin.cycles.emails.configurations.list.queryKey({
									cycleId: variables.cycleId,
								}),
								exact: false,
								refetchType: 'all',
							})
						},
					}),
				)

				return {
					deleteConfiguration: ({ onSuccess, ...input }) => mutation.mutate(input, { onSuccess }),
					isDeleting: mutation.isPending,
				}
			},

			useSetEnabled: () => {
				const queryClient = useQueryClient()
				const toast = useToast()

				const mutation = useMutation(
					trpc.admin.cycles.emails.configurations.setEnabled.mutationOptions({
						onError: (error) => {
							toast.showToast(error.message ?? 'Failed to update email configuration', 'error')
						},
						onSettled: (_data, _error, variables) => {
							queryClient.invalidateQueries({
								queryKey: trpc.admin.cycles.emails.configurations.list.queryKey({
									cycleId: variables.cycleId,
								}),
								exact: false,
								refetchType: 'all',
							})
						},
					}),
				)

				return {
					setEnabled: (input) => mutation.mutate(input),
					isSettingEnabled: mutation.isPending,
				}
			},
		})),
		{ name: 'cycle-email-configurations-store' },
	),
)
